import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import authActionCreators from "~/store/auth/actions";
import authSelectors from "~/store/auth/selectors";
import AuthForm from "~/forms/AuthForm";
import authSchema from "~/forms/AuthForm/authSchema";
import { StatusStateEnum } from "~/types/enums";
import logo from "~/assets/i/logo.svg";
import { Button, Alert, Link, Modal } from "~/components-shared";
import Footer from "~/components/Footer";
import PasswordResetModal from "~/components/PasswordResetModal";
import qs from "qs";
import { useLocation } from "react-router-dom";
import Markdown from "~/components/Markdown";
const Section = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
`;
const Branding = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 7;
}}px;
`;
const Logo = styled.img`
  margin-right: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing.step;
}}px;
`;
const Content = styled.div`
  padding: 1em;
  display: flex;
  overflow-y: auto;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Wrapper = styled.div`
  max-width: 280px;
  width: 100%;
  height: 100%;
  display: flex;
  justify-items: center;
  align-items: center;
  flex-direction: column;
`;
const Auth = () => {
  const {
    t
  } = useTranslation();
  const {
    search
  } = useLocation();
  const {
    email
  } = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const defaultValues = {
    email: email || "",
    password: ""
  };
  const {
    handleSubmit,
    control,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(authSchema),
    defaultValues
  });
  const dispatch = useDispatch();
  const error = useSelector(authSelectors.getError);
  const status = useSelector(authSelectors.getStatus);
  const expired = useSelector(authSelectors.getSessionStatus) === StatusStateEnum.EXPIRED ? t("Auth.tokenExpired") : undefined;
  const [resetOpen, setResetOpen] = useState(false);
  const onSubmit = handleSubmit(data => dispatch(authActionCreators.fetchStarted(data)));
  useEffect(() => {
    if (email) {
      reset(defaultValues);
    }
  }, [email]);
  const handleForgotPassword = () => {
    setResetOpen(true);
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Branding, null, /*#__PURE__*/React.createElement(Logo, {
    src: logo,
    alt: "Joyn",
    width: 40
  }), /*#__PURE__*/React.createElement("h2", null, "Joyn")), /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error || expired && /*#__PURE__*/React.createElement(Markdown, {
    text: error || expired
  })), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(AuthForm, {
    control: control
  }), /*#__PURE__*/React.createElement(Button, {
    color: "secondary",
    s: {
      mt: 3,
      mb: 3
    },
    maxWidth: "280px",
    type: "submit",
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true
  }, t("Auth.login")), /*#__PURE__*/React.createElement(Link, {
    onClick: handleForgotPassword,
    weight: "semiBold"
  }, t("Auth.forgotPassword"))))), /*#__PURE__*/React.createElement(Footer, null), /*#__PURE__*/React.createElement(Modal, {
    open: resetOpen
  }, /*#__PURE__*/React.createElement(PasswordResetModal, {
    email: watch("email"),
    onClose: () => setResetOpen(false)
  })));
};
export default Auth;